import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminOrders } from '@/api/admin';
import AdminHeader from '@/components/AdminHeader';
import { ORDER_STATUS_CONFIG } from '@/types/order';
import type { OrderStatus, OrderDetail } from '@/types/order';

type FilterKey = 'all' | OrderStatus;

const statusTabs: { key: FilterKey; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'pending', label: '주문대기' },
  { key: 'paid', label: '결제완료' },
  { key: 'processing', label: '제작중' },
  { key: 'completed', label: '완료' },
  { key: 'cancelled', label: '취소' },
];

const PAGE_SIZE = 20;

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FilterKey>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState<OrderDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const res = await getAdminOrders(page, PAGE_SIZE);
      setOrders(res.orders);
      setTotal(res.total);
    } catch {
      setOrders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage, fetchOrders]);

  const handleTabChange = (tab: FilterKey) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const filteredOrders = activeTab === 'all'
    ? orders
    : orders.filter(order => order.status === activeTab);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const getStatusBadgeColor = (status: string) => {
    const config = ORDER_STATUS_CONFIG[status as OrderStatus];
    if (config) return config.color;
    return 'bg-slate-100 text-slate-700';
  };

  const getStatusCount = (status: FilterKey) => {
    if (status === 'all') return orders.length;
    return orders.filter(order => order.status === status).length;
  };

  // 페이지 번호 목록 생성
  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-slate-500">불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">주문 목록</h2>
          <p className="text-sm text-slate-600">고객 주문을 확인하고 AI 이미지 생성 작업을 진행하세요</p>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="flex border-b border-slate-200">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap relative ${
                  activeTab === tab.key
                    ? 'text-amber-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {getStatusCount(tab.key)}
                </span>
                {activeTab === tab.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-600"></div>
                )}
              </button>
            ))}
          </div>

          {/* Orders Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">주문번호</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 whitespace-nowrap">상태</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">고객명</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">연락처</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">이메일</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">플랜</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">결제금액</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">주문일시</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-sm text-slate-500">
                        <i className="ri-inbox-line text-4xl text-slate-300 mb-3 block"></i>
                        해당 상태의 주문이 없습니다
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr
                        key={order.orderId}
                        onClick={() => navigate(`/admin/orders/${order.orderIdx}`)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900 whitespace-nowrap">{order.orderId}</td>
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(order.status)}`}>
                            {order.statusLabel || ORDER_STATUS_CONFIG[order.status]?.label || order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">{order.customerName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{order.customerPhone}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">{(order as any).email || order.customerEmail}</td>
                        <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">{order.productName}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900 text-right whitespace-nowrap">
                          {order.amount.toLocaleString()}원
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                          {new Date(order.createDt).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                총 <strong className="text-slate-700">{total}</strong>건 중 {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, total)}건
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {getPageNumbers().map((page, idx) =>
                  page === '...' ? (
                    <span key={`dot-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs text-slate-400">...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-amber-500 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">전체 주문</span>
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                <i className="ri-file-list-3-line text-slate-600"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{total}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">대기중</span>
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-slate-600"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{getStatusCount('pending') + getStatusCount('paid')}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-amber-600">진행중</span>
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <i className="ri-loader-4-line text-amber-600"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-amber-600">{getStatusCount('processing')}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-emerald-600">완료</span>
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-emerald-600"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{getStatusCount('completed')}</p>
          </div>
        </div>
      </main>
    </div>
  );
}
